import { useState, useEffect, createContext, useContext, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import { ThemeProvider } from "./contexts/ThemeContext";
import { UserSheetProvider } from "./contexts/UserSheetContext";
import { userManager, isRedirectCallback, cleanCallbackUrl } from "./config/oidcConfig";

// Pages
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import LeavesPage from "./pages/LeavesPage";
import AbsencesPage from "./pages/AbsencesPage";
import MeetingsPage from "./pages/MeetingsPage";
import PayslipsPage from "./pages/PayslipsPage";
import NotificationsPage from "./pages/NotificationsPage";
import UsersPage from "./pages/UsersPage";
import ProfilePage from "./pages/ProfilePage";
import CalendarPage from "./pages/CalendarPage";
import NAOPage from "./pages/NAOPage";
import TrombPage from "./pages/TrombPage";
import NDFPage from "./pages/NDFPage";
import OnboardingPage from "./pages/OnboardingPage";
import AuditPage from "./pages/AuditPage";

// Layout
import MainLayout from "./components/layout/MainLayout";

// API base URL
export const API = process.env.REACT_APP_BACKEND_URL || "/api";

// Roles constants
export const ROLES_FULL_ACCESS = ["admin", "hr"];
export const ROLES_MANAGEMENT = ["admin", "hr", "manager", "comptabilité"];
export const ROLES_COMPTA = ["admin", "hr", "comptabilité"];

// ScrollToTop
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

// Setup axios auth interceptor (called once)
let interceptorsSetup = false;
const setupAxiosInterceptors = (getToken, onUnauthorized) => {
  if (interceptorsSetup) return;
  interceptorsSetup = true;

  axios.interceptors.request.use((config) => {
    const token = getToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  });

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) onUnauthorized();
      return Promise.reject(error);
    }
  );
};

// Protected Route
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, isAuthenticated } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (allowedRoles && !allowedRoles.includes(user?.role))
    return <Navigate to="/dashboard" replace />;
  return children;
};

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const tokenRef = useRef(null);

  const setAuth = (newToken, newUser) => {
    tokenRef.current = newToken;
    if (newToken) {
      localStorage.setItem("token", newToken);
    } else {
      localStorage.removeItem("token");
    }
    setToken(newToken);
    setUser(newUser);
  };

  const logout = () => {
    setAuth(null, null);
    // Ferme aussi la session Keycloak, sinon un nouveau « se connecter »
    // reconnecterait silencieusement sans repasser par l'écran de connexion.
    userManager.signoutRedirect().catch(() => {
      window.location.href = "/";
    });
  };

  const login = async () => {
    // La page navigue vers Keycloak, qui redirige lui-même vers Microsoft.
    await userManager.signinRedirect();
  };

  useEffect(() => {
    // Setup axios interceptor with live token getter
    setupAxiosInterceptors(
      () => tokenRef.current || localStorage.getItem("token"),
      () => setAuth(null, null)
    );

    const init = async () => {
      try {
        if (isRedirectCallback()) {
          const oidcUser = await userManager.signinRedirectCallback();
          // Nettoyer l'URL avant l'échange : si celui-ci échoue, un
          // rechargement ne doit pas rejouer un code déjà consommé.
          cleanCallbackUrl();
          const response = await axios.post(`${API}/auth/oidc`, {
            access_token: oidcUser.access_token,
          });
          const { access_token, user: userData } = response.data;
          setAuth(access_token, userData);
          setIsLoading(false);
          return;
        }
      } catch (err) {
        console.error("[Auth] échec de la connexion Keycloak :", err);
        cleanCallbackUrl();
        localStorage.removeItem("token");
      }

      // No redirect result — restore from stored token
      const storedToken = localStorage.getItem("token");
      if (storedToken) {
        tokenRef.current = storedToken;
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${storedToken}` },
          });
          setAuth(storedToken, response.data);
        } catch {
          setAuth(null, null);
        }
      }

      setIsLoading(false);
    };

    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshUser = async () => {
    const storedToken = tokenRef.current || localStorage.getItem("token");
    if (!storedToken) return;
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${storedToken}` },
      });
      setUser(response.data);
    } catch {
      // Silencieux — ne pas déconnecter l'utilisateur sur un échec de refresh
    }
  };

  const isDark =
    localStorage.getItem("theme") === "dark" ||
    (!localStorage.getItem("theme") &&
      window.matchMedia("(prefers-color-scheme: dark)").matches);

  if (isLoading) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center ${
          isDark ? "bg-[#060E1C]" : "bg-[#F9FAFB]"
        }`}
      >
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div
            className={`w-16 h-16 rounded-full ${
              isDark ? "bg-[#4B8BF4]/20" : "bg-[#1A4D2E]/20"
            }`}
          />
          <p className={isDark ? "text-[#7EA8CE]" : "text-[#4B5563]"}>
            Chargement...
          </p>
        </div>
      </div>
    );
  }

  return (
    <ThemeProvider>
      <AuthContext.Provider
        value={{
          user,
          token,
          isAuthenticated: !!token && !!user,
          login,
          logout,
          refreshUser,
        }}
      >
        <UserSheetProvider>
        <Toaster position="top-right" richColors />
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            {/* Login à la racine */}
            <Route
              path="/"
              element={
                token && user ? <Navigate to="/dashboard" replace /> : <LoginPage />
              }
            />

            {/* App routes — layout sans préfixe d'URL */}
            <Route
              element={
                <ProtectedRoute>
                  <MainLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/leaves" element={<LeavesPage />} />
              <Route path="/absences" element={<AbsencesPage />} />
              <Route path="/meetings" element={<MeetingsPage />} />
              <Route path="/payslips" element={<PayslipsPage />} />
              <Route path="/calendar" element={<CalendarPage />} />
              <Route path="/notifications" element={<NotificationsPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/trombinoscope" element={<TrombPage />} />
              <Route path="/ndf" element={<NDFPage />} />
              <Route
                path="/users"
                element={
                  <ProtectedRoute allowedRoles={["admin", "hr", "manager", "comptabilité"]}>
                    <UsersPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nao"
                element={
                  <ProtectedRoute allowedRoles={["admin", "hr"]}>
                    <NAOPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/onboarding"
                element={
                  <ProtectedRoute allowedRoles={["admin", "hr"]}>
                    <OnboardingPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/audit"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <AuditPage />
                  </ProtectedRoute>
                }
              />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        </UserSheetProvider>
      </AuthContext.Provider>
    </ThemeProvider>
  );
}

export default App;
